import { useState } from 'react'
import {
  CheckCircleIcon,
  PaperAirplaneIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/solid'
import { motion } from 'framer-motion'
import { submitContactForm } from '@/lib/queries/admin'

const CATEGORIES = [
  { value: 'general', label: 'General Feedback' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'content', label: 'Content / Accuracy' },
  { value: 'prayer_request', label: 'Prayer Request' },
]

export default function FeedbackForm({ compact = false }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    category: 'general',
    rating: 0,
    message: '',
    recommendation: '',
  })
  const [hoverRating, setHoverRating] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [succeeded, setSucceeded] = useState(false)
  const [error, setError] = useState('')

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (!form.message.trim()) {
      setError('Please share your feedback before submitting.')
      return
    }
    if (form.rating === 0) {
      setError('Please select a rating.')
      return
    }

    setSubmitting(true)
    try {
      await submitContactForm('feedback', {
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        category: form.category,
        rating: form.rating,
        message: form.message.trim(),
        recommendation: form.recommendation.trim() || null,
      })
      setSucceeded(true)
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--outline-ghost)] text-[var(--text-primary)] placeholder:text-[var(--text-muted-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)] focus:border-[var(--gold)] transition-all'

  if (succeeded) {
    return (
      <section
        id="feedback"
        aria-label="Feedback submitted"
        className={
          compact ? 'py-10 px-4' : 'py-20 md:py-28 px-4 md:px-8 bg-[var(--section-alt-bg)]'
        }
      >
        <div className="max-w-xl mx-auto glass-panel p-8 md:p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[var(--gold)]/20 flex items-center justify-center">
            <CheckCircleIcon className="w-8 h-8 text-[var(--gold)]" aria-hidden="true" />
          </div>
          <h3 className="font-heading text-[var(--text-primary)] text-2xl md:text-3xl font-bold mb-3">
            Thank You!
          </h3>
          <p className="text-[var(--text-muted)] leading-relaxed">
            Your feedback means the world to us. We read every submission and use it to make
            Illuminate Faith better for the faith community.
          </p>
        </div>
      </section>
    )
  }

  return (
    <section
      id="feedback"
      aria-label="Share your feedback"
      className={compact ? 'py-10 px-4' : 'py-20 md:py-28 px-4 md:px-8 bg-[var(--section-alt-bg)]'}
    >
      <motion.div
        className="max-w-xl mx-auto"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="glass-panel p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="section-badge mx-auto mb-4">
              <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" aria-hidden="true" />
              Your Voice Matters
            </div>
            <h2 className="font-heading text-[var(--gold)] text-2xl md:text-3xl font-bold mb-3">
              Share Your Feedback
            </h2>
            <p className="text-[var(--text-muted)] leading-relaxed">
              Help us build something that truly serves the faith community. Every piece of feedback
              shapes what comes next.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name & Email row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                aria-label="Your name (optional)"
                placeholder="Your Name (optional)"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                className={inputClass}
              />
              <input
                type="email"
                aria-label="Your email (optional)"
                placeholder="Your Email (optional)"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                className={inputClass}
              />
            </div>

            {/* Category */}
            <select
              value={form.category}
              onChange={e => update('category', e.target.value)}
              aria-label="Feedback category"
              className={inputClass + ' cursor-pointer'}
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            {/* Star Rating */}
            <div>
              <label className="block text-[var(--text-muted)] text-sm mb-2">
                How would you rate your experience?
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => update('rating', star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                  >
                    <StarIcon
                      className={`w-7 h-7 transition-colors ${
                        star <= (hoverRating || form.rating)
                          ? 'text-[var(--gold)] fill-[var(--gold)]'
                          : 'text-[var(--text-muted-soft)]'
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Message */}
            <textarea
              aria-label="Your feedback message"
              placeholder="Share your thoughts, suggestions, or experience..."
              value={form.message}
              onChange={e => update('message', e.target.value)}
              required
              rows={4}
              className={inputClass + ' resize-none'}
            />

            {/* Recommendation */}
            <textarea
              aria-label="Feature or idea recommendation (optional)"
              placeholder="Any features or ideas you'd recommend? (optional)"
              value={form.recommendation}
              onChange={e => update('recommendation', e.target.value)}
              rows={2}
              className={inputClass + ' resize-none'}
            />

            {/* Error */}
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={submitting}
              className="w-full bg-[var(--gold)] text-[var(--text-on-gold)] font-bold py-3.5 rounded-xl text-lg transition-shadow duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              whileHover={{ y: -2, boxShadow: '0 8px 30px var(--glow-color)' }}
              whileTap={{ scale: 0.97 }}
            >
              {submitting ? (
                'Submitting...'
              ) : (
                <>
                  <PaperAirplaneIcon className="w-5 h-5" aria-hidden="true" />
                  Submit Feedback
                </>
              )}
            </motion.button>
          </form>
        </div>
      </motion.div>
    </section>
  )
}
